import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Check, Clock, ChevronDown, ChevronUp,
  Sprout, Sun, Package, Droplets, Scissors, Search,
  ArrowDownAZ, ArrowUpAZ, TrendingUp, Eye
} from 'lucide-react';
import { getPlantEmoji, getPlantImage } from '@/lib/plantEmojis';
import { LegalFooter } from '@/components/LegalFooter';
import { TaskDetailDialog } from '@/components/TaskDetailDialog';
import { GerminationCheckDialog } from '@/components/GerminationCheckDialog';
import { format, parseISO, isAfter, isBefore, startOfDay } from 'date-fns';

const STAGE_ICONS: Record<string, typeof Sprout> = {
  seed_sowing: Sprout, chit: Sprout, first_transplant: Sprout,
  second_transplant: Sprout, harden_off: Sun, plant_out: Sun,
  germination_check: Eye,
  harvest: Package, harvest_ongoing: Package, feed: Droplets,
  spring_feed: Droplets, prune: Scissors, autumn_cut_back: Scissors,
  support: Sun, earth_up: Sun, thin_out: Scissors, thin: Scissors,
  pinch: Scissors, mulch: Sun, weed: Scissors, preserve: Package,
  summer_prune: Scissors, winter_prune: Scissors,
};

const STAGE_ORDER = [
  'chit', 'seed_sowing', 'germination_check', 'first_transplant', 'second_transplant',
  'harden_off', 'plant_out', 'thin_out', 'thin', 'pinch', 'support',
  'earth_up', 'feed', 'spring_feed', 'mulch', 'weed',
  'harvest', 'harvest_ongoing', 'prune', 'summer_prune', 'winter_prune',
  'autumn_cut_back', 'preserve',
];

interface Task {
  id: string;
  task_type: string;
  title: string;
  description: string | null;
  scheduled_date: string;
  estimated_minutes: number | null;
  completed: boolean | null;
  user_plant_id: string;
}

interface UserPlantWithDetails {
  id: string;
  plant_id: string;
  status: string | null;
  plants: {
    name: string;
    plant_type: string;
    category: string;
  };
}

interface PlantProgress {
  plant: UserPlantWithDetails;
  tasks: Task[];
  completedCount: number;
  totalCount: number;
  currentStage: string | null;
  nextTask: Task | null;
}

export default function Progress() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [userPlants, setUserPlants] = useState<UserPlantWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedPlant, setExpandedPlant] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'in-progress' | 'upcoming'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortMode, setSortMode] = useState<'az' | 'progress'>('az');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    loadData();
  }, [user]);

  const loadData = async () => {
    const [tasksRes, plantsRes] = await Promise.all([
      supabase.from('garden_tasks').select('*').eq('user_id', user!.id).order('scheduled_date'),
      supabase.from('user_plants').select('id, plant_id, status, plants(name, plant_type, category)').eq('user_id', user!.id),
    ]);
    if (tasksRes.data) setTasks(tasksRes.data as Task[]);
    if (plantsRes.data) setUserPlants(plantsRes.data as unknown as UserPlantWithDetails[]);
    setLoading(false);
  };

  const today = startOfDay(new Date());
  const todayStr = format(today, 'yyyy-MM-dd');

  const plantProgressList = useMemo((): PlantProgress[] => {
    return userPlants.map(plant => {
      const plantTasks = tasks.filter(t => t.user_plant_id === plant.id);
      const completedCount = plantTasks.filter(t => t.completed).length;
      const totalCount = plantTasks.length;

      const incompleteTasks = plantTasks
        .filter(t => !t.completed)
        .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));

      const nextTask = incompleteTasks[0] || null;
      const currentStage = nextTask?.task_type || null;

      return { plant, tasks: plantTasks, completedCount, totalCount, currentStage, nextTask };
    })
    .filter(p => p.totalCount > 0);
  }, [tasks, userPlants]);

  const filteredPlants = useMemo(() => {
    let list = plantProgressList;

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(p => p.plant.plants.name.toLowerCase().includes(q));
    }

    // Status filter
    if (filter === 'in-progress') {
      list = list.filter(p => p.completedCount > 0 && p.completedCount < p.totalCount);
    } else if (filter === 'upcoming') {
      list = list.filter(p => p.completedCount === 0);
    }

    // Sort
    if (sortMode === 'az') {
      list = [...list].sort((a, b) => a.plant.plants.name.localeCompare(b.plant.plants.name));
    } else {
      list = [...list].sort((a, b) => {
        const aP = a.totalCount > 0 ? a.completedCount / a.totalCount : 0;
        const bP = b.totalCount > 0 ? b.completedCount / b.totalCount : 0;
        return aP - bP;
      });
    }

    return list;
  }, [plantProgressList, filter, searchQuery, sortMode]);

  const overallCompleted = tasks.filter(t => t.completed).length;
  const overallTotal = tasks.length;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Sprout className="h-8 w-8 animate-pulse text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/40 glass-header sticky top-0 z-10">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="rounded-xl">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-foreground tracking-tight">My Garden Progress</h1>
              <p className="text-xs text-muted-foreground">Track every plant's journey</p>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-6 space-y-6">
        {/* Overall progress */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card className="card-organic overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-foreground">Season Progress</h2>
                <span className="text-2xl font-bold text-primary">
                  {overallTotal > 0 ? Math.round((overallCompleted / overallTotal) * 100) : 0}%
                </span>
              </div>
              <div className="h-3 w-full rounded-full bg-muted overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${overallTotal > 0 ? (overallCompleted / overallTotal) * 100 : 0}%` }}
                  transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
                  className="h-full rounded-full garden-gradient"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {overallCompleted} of {overallTotal} tasks completed across {plantProgressList.length} plants
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Search + Filter + Sort */}
        <div className="space-y-3">
          {/* Search bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search plants..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 rounded-xl bg-muted/30 border-border/40 h-9 text-sm"
            />
          </div>

          {/* Filter chips + sort toggle */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {(['all', 'in-progress', 'upcoming'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${
                    filter === f
                      ? 'bg-primary text-primary-foreground shadow-soft'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {f === 'all' ? 'All Plants' : f === 'in-progress' ? 'In Progress' : 'Not Started'}
                </button>
              ))}
            </div>
            <button
              onClick={() => setSortMode(s => s === 'az' ? 'progress' : 'az')}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-medium text-muted-foreground bg-muted/50 hover:bg-muted transition-all"
              title={sortMode === 'az' ? 'Sorted A-Z' : 'Sorted by progress'}
            >
              {sortMode === 'az' ? <ArrowDownAZ className="h-3.5 w-3.5" /> : <TrendingUp className="h-3.5 w-3.5" />}
              {sortMode === 'az' ? 'A-Z' : 'Progress'}
            </button>
          </div>
        </div>

        {/* Plant cards */}
        <div className="space-y-3">
          {filteredPlants.map((pp, i) => (
            <PlantProgressCard
              key={pp.plant.id}
              pp={pp}
              index={i}
              expanded={expandedPlant === pp.plant.id}
              onToggle={() => setExpandedPlant(
                expandedPlant === pp.plant.id ? null : pp.plant.id
              )}
              todayStr={todayStr}
              onTaskClick={(task) => {
                setSelectedTask(task);
                setTaskDialogOpen(true);
              }}
            />
          ))}

          {filteredPlants.length === 0 && (
            <Card className="card-organic border-dashed border-2 border-primary/20">
              <CardContent className="py-12 text-center">
                <Sprout className="h-8 w-8 text-primary/40 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No plants match this filter</p>
              </CardContent>
            </Card>
          )}
        </div>

        {selectedTask?.task_type === 'germination_check' ? (
          <GerminationCheckDialog
            task={selectedTask}
            open={taskDialogOpen}
            onOpenChange={setTaskDialogOpen}
            onTaskUpdated={loadData}
            userId={user!.id}
          />
        ) : (
          <TaskDetailDialog
            task={selectedTask}
            open={taskDialogOpen}
            onOpenChange={setTaskDialogOpen}
            onTaskUpdated={loadData}
          />
        )}
      </main>

      <LegalFooter />
    </div>
  );
}

function PlantProgressCard({
  pp, index, expanded, onToggle, todayStr, onTaskClick
}: {
  pp: PlantProgress;
  index: number;
  expanded: boolean;
  onToggle: () => void;
  todayStr: string;
  onTaskClick: (task: Task) => void;
}) {
  const plantName = pp.plant.plants.name;
  const emoji = getPlantEmoji(plantName);
  const customImage = getPlantImage(plantName);
  const progress = pp.totalCount > 0 ? (pp.completedCount / pp.totalCount) * 100 : 0;
  const isComplete = pp.completedCount === pp.totalCount;

  // Group tasks by stage type, maintaining lifecycle order
  const stageGroups = useMemo(() => {
    const grouped = new Map<string, Task[]>();
    for (const task of pp.tasks) {
      const existing = grouped.get(task.task_type) || [];
      existing.push(task);
      grouped.set(task.task_type, existing);
    }
    // Sort by stage order
    const sorted = [...grouped.entries()].sort(([a], [b]) => {
      const ai = STAGE_ORDER.indexOf(a);
      const bi = STAGE_ORDER.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
    return sorted;
  }, [pp.tasks]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <Card className="card-organic overflow-hidden">
        {/* Header — always visible */}
        <button
          onClick={onToggle}
          className="w-full text-left p-4 flex items-center gap-3 hover:bg-muted/20 transition-colors"
        >
          {/* Plant icon */}
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 shrink-0">
            {customImage ? (
              <img src={customImage} alt={plantName} className="h-7 w-7 object-contain" />
            ) : (
              <span className="text-xl">{emoji}</span>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-foreground truncate">{plantName}</h3>
              {isComplete && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-primary/10 text-primary border-0">
                  Done ✓
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden max-w-[120px]">
                <div
                  className="h-full rounded-full garden-gradient transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-[11px] text-muted-foreground shrink-0">
                {pp.completedCount}/{pp.totalCount}
              </span>
            </div>
          </div>

          {/* Current stage hint */}
          {pp.nextTask && !isComplete && (
            <div className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/40 rounded-xl px-2.5 py-1.5 shrink-0">
              <Clock className="h-3 w-3" />
              <span className="truncate max-w-[140px]">
                Next: {formatStageLabel(pp.nextTask.task_type)}
              </span>
            </div>
          )}

          {/* Expand arrow */}
          <div className="shrink-0 text-muted-foreground">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </button>

        {/* Expanded timeline */}
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="border-t border-border/40"
          >
            <div className="p-4 space-y-1">
              {stageGroups.map(([stageType, stageTasks]) => {
                const StageIcon = STAGE_ICONS[stageType] || Sprout;
                const allCompleted = stageTasks.every(t => t.completed);
                const someCompleted = stageTasks.some(t => t.completed);
                const completedInStage = stageTasks.filter(t => t.completed).length;
                const isRecurring = stageTasks.length > 1;
                const earliestDate = stageTasks[0]?.scheduled_date;
                const isPast = earliestDate < todayStr;
                const isCurrent = !allCompleted && (someCompleted || (isPast && !allCompleted));

                // Use actual task title (stripped of plant name) for more descriptive labels
                const rawLabel = stageTasks[0]?.title?.replace(`${plantName}: `, '') || '';
                const stageLabel = rawLabel || formatStageLabel(stageType);

                return (
                  <button
                    key={stageType}
                    onClick={() => {
                      // Open the first incomplete task, or the first task if all completed
                      const targetTask = stageTasks.find(t => !t.completed) || stageTasks[0];
                      if (targetTask) onTaskClick(targetTask);
                    }}
                    className={`w-full flex items-start gap-3 py-2.5 px-3 rounded-xl transition-colors text-left hover:bg-muted/30 cursor-pointer ${
                      isCurrent ? 'bg-primary/5 border border-primary/15' :
                      allCompleted ? 'opacity-70' : ''
                    }`}
                  >
                    {/* Status indicator */}
                    <div className={`mt-0.5 flex h-7 w-7 items-center justify-center rounded-full shrink-0 ${
                      allCompleted ? 'bg-primary/15 text-primary' :
                      isCurrent ? 'bg-primary/10 text-primary ring-2 ring-primary/20' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {allCompleted ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : (
                        <StageIcon className="h-3.5 w-3.5" />
                      )}
                    </div>

                    {/* Stage info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${
                          allCompleted ? 'text-muted-foreground line-through' :
                          isCurrent ? 'text-foreground' : 'text-foreground'
                        }`}>
                          {stageLabel}
                        </span>
                        {isRecurring && (
                          <span className="text-[10px] text-muted-foreground bg-muted/60 rounded-md px-1.5 py-0.5">
                            {completedInStage}/{stageTasks.length}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {allCompleted ? (
                          `Completed${isRecurring ? ` (${completedInStage} sessions)` : ''}`
                        ) : isCurrent ? (
                          isRecurring
                            ? `${completedInStage} done, ${stageTasks.length - completedInStage} remaining`
                            : `Due ${formatTaskDate(earliestDate, todayStr)}`
                        ) : (
                          `Scheduled ${formatTaskDate(earliestDate, todayStr)}`
                        )}
                      </p>
                    </div>

                    {/* Date badge */}
                    {!allCompleted && earliestDate && (
                      <span className={`text-[10px] shrink-0 px-2 py-1 rounded-lg ${
                        earliestDate <= todayStr
                          ? 'bg-accent/10 text-accent-foreground font-medium'
                          : 'bg-muted/40 text-muted-foreground'
                      }`}>
                        {format(parseISO(earliestDate), 'd MMM')}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </Card>
    </motion.div>
  );
}

function formatStageLabel(stageType: string): string {
  const labels: Record<string, string> = {
    chit: 'Chit',
    seed_sowing: 'Sow Seeds',
    first_transplant: 'First Transplant',
    second_transplant: 'Second Transplant',
    harden_off: 'Harden Off',
    plant_out: 'Plant Out',
    thin_out: 'Thin Out',
    thin: 'Thin',
    pinch: 'Pinch Out',
    support: 'Support & Stake',
    earth_up: 'Earth Up',
    feed: 'Feed',
    spring_feed: 'Spring Feed',
    mulch: 'Mulch',
    weed: 'Weed',
    harvest: 'Harvest',
    harvest_ongoing: 'Harvest (ongoing)',
    prune: 'Prune',
    summer_prune: 'Summer Prune',
    winter_prune: 'Winter Prune',
    autumn_cut_back: 'Autumn Cut Back',
    preserve: 'Preserve',
  };
  return labels[stageType] || stageType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatTaskDate(dateStr: string, todayStr: string): string {
  if (dateStr === todayStr) return 'today';
  const date = parseISO(dateStr);
  const today = parseISO(todayStr);
  if (isBefore(date, today)) return 'overdue';
  return format(date, 'd MMM');
}
